import React, { useState } from 'react';
import Button from '@/components/ui/button';
import {
  ChatMessage,
  getChatOrderTemplate,
} from '@/infrastructure/domain/chat';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Avatar } from 'antd';
import MessageHighlighter from '../../utils/MessageHighlighter';
import { useContext } from 'react';
import { ChatContext } from '@/context/Chat';
import { useUpdateOrderStatus } from '@/hooks/order/useUpdateOrderStatus';
import { OrderActionStatusMap } from '@/infrastructure/domain/order';
import { OrderAction } from '@/infrastructure/api/order';
import Swal from 'sweetalert2';
import { useEntityNavigation } from '@/hooks/utils/useEntityNavigationLink';
import Image from '@/components/ui/images/Image';
import { useLoggedUser } from '@/hooks/user/useLoggedUser';
import { useRefundModal } from '@/context/refund-modal/RefundModal';
import RatingModal from '@/components/shared/rating/RatingModal';
import { EntityType } from '@/infrastructure/domain/entity';

type Props = {
  chatMessage: ChatMessage;
};

const ChatMessageItem = ({ chatMessage }: Props) => {
  const { userType, entityId } = useLoggedUser();
  const { navigateTo } = useEntityNavigation();
  const { setIsChatOpen } = useContext(ChatContext);
  const updateOrderStatus = useUpdateOrderStatus(chatMessage.roomId);
  const { openRefundModal } = useRefundModal();
  const [ratingModalVisible, setRatingModalVisible] = useState(false);

  const isMessageSender = chatMessage.senderId === entityId;

  const orderTemplate = getChatOrderTemplate(
    chatMessage,
    (userType as EntityType) || 'MARKET',
  );

  const handleOnOrderItemClick = () => {
    setIsChatOpen(false);
    navigateTo(`/orders/${orderTemplate.order.id}`);
  };

  const handleOnOrderAction = async (type: string) => {
    // Handle rate and refund actions with modals
    if (type === 'rate') {
      setRatingModalVisible(true);
      return;
    }

    if (type === 'refund') {
      openRefundModal(orderTemplate.order.id);
      return;
    }

    // Handle other order status updates
    let orderAction: OrderAction;
    if (type === 'cancel') {
      orderAction = 'CANCEL_ORDER';
    } else {
      orderAction = OrderActionStatusMap[orderTemplate.order.status];
    }

    if (!updateOrderStatus) {
      Swal.fire({
        icon: 'error',
        title: 'Unable to update order',
        text: 'Please try again later. If issue persist contact support.',
      });
      return;
    }

    try {
      await updateOrderStatus.mutateAsync({
        orderId: orderTemplate.order.id,
        payload: {
          orderAction,
          context: {
            origin: 'chatbox',
            data: chatMessage,
          },
        },
      });

      Swal.fire({
        icon: 'success',
        title: 'Order status updated!',
        text: `You have updated the order ${orderTemplate.order.number}.`,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Unable to update order',
        text: 'Please try again later. If issue persist contact support.',
      });
    }
  };

  return (
    <>
      <div
        key={chatMessage.id}
        className={`flex items-start gap-1 items-start text-sm ${
          isMessageSender ? 'justify-end' : 'justify-start'
        }`}
      >
        {!isMessageSender && <Avatar size={16} />}
        <div
          className={`p-3 rounded ${isMessageSender ? 'bg-amber-50' : 'bg-white'} shadow-md`}
        >
          {chatMessage.senderType === 'system' && (
            <p className="text-gray-400 mb-1">System:</p>
          )}
          {chatMessage.type === 'text' && <p>{chatMessage.plainText}</p>}
          {chatMessage.type === 'order' && (
            <>
              <MessageHighlighter message={orderTemplate.activity.message} />
              {orderTemplate.showLineItems && (
                <>
                  <p className="font-bold my-2">Order details:</p>
                  <div
                    className="flex hover:bg-gray-100 p-2 cursor-pointer"
                    onClick={handleOnOrderItemClick}
                  >
                    <Image
                      imageSource={orderTemplate.lineItem.product.images[0]}
                      className="w-[64px] h-[64px] bg-gray-500 rounded mr-2"
                    />
                    <div>
                      <p className="font-semibold">
                        {orderTemplate.lineItem.product.name}
                      </p>
                      <p className="text-gray-500">
                        x{orderTemplate.lineItem.quantity}{' '}
                        {orderTemplate.lineItem.variant.unit.name}
                      </p>
                      <p className="text-gray-500">
                        Order No: {orderTemplate.order.number}
                      </p>
                      {orderTemplate.hasMoreItems && <p>contains more items</p>}
                    </div>
                  </div>
                </>
              )}
              {orderTemplate.showActions && !chatMessage.actioned && (
                <div className="mt-4 w-full flex gap-2">
                  {orderTemplate.activity.actions.map(action => (
                    <Button
                      key={action.type}
                      variant={`${action.type === 'refund' ? 'primary' : action.type === 'rate' ? 'secondary' : !['cancel'].includes(action.type) ? 'primary' : 'secondary'}`}
                      disabled={action.isDisabled}
                      onClick={() => handleOnOrderAction(action.type)}
                    >
                      {action.text}
                    </Button>
                  ))}
                </div>
              )}
              {chatMessage.actioned && (
                <div className="flex items-center gap-1 text-green-800 mt-2">
                  <CheckCircleOutlined />
                  {orderTemplate.order.status === 'PENDING'
                    ? 'Order Confirmed'
                    : 'Order Completed'}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Rating Modal */}
      {ratingModalVisible && orderTemplate.order && orderTemplate.lineItems && (
        <RatingModal
          visible={ratingModalVisible}
          onClose={() => setRatingModalVisible(false)}
          order={{
            ...orderTemplate.order,
            lineItems: orderTemplate.lineItems,
          }}
        />
      )}
    </>
  );
};

export default React.memo(ChatMessageItem);
